import type { RadioGroupProps } from 'antd/lib';
import { useMemo } from 'react';
import { Control, FieldPath, FieldValues, useWatch } from 'react-hook-form';

type UseBankNamePreviewParams<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues, any, TFieldValues>;
  bankNameField: FieldPath<TFieldValues>;
  bankTypeField: FieldPath<TFieldValues>;
  bankNameSuffixField: FieldPath<TFieldValues>;
  bankTypeOptions: RadioGroupProps['options'];
  noneLabel?: string;
};

/**
 * Build bank preview value with order: bank_name + bank_type_label + bank_name_suffix.
 * Field paths are configurable so this hook can be reused across forms.
 */
export const useBankNamePreview = <TFieldValues extends FieldValues>({
  control,
  bankNameField,
  bankTypeField,
  bankNameSuffixField,
  bankTypeOptions,
  noneLabel = 'なし',
}: UseBankNamePreviewParams<TFieldValues>): string => {
  const bankName = useWatch<TFieldValues, FieldPath<TFieldValues>>({
    control,
    name: bankNameField,
  });
  const bankTypeId = useWatch<TFieldValues, FieldPath<TFieldValues>>({
    control,
    name: bankTypeField,
    defaultValue: undefined,
  });
  const bankNameSuffix = useWatch<TFieldValues, FieldPath<TFieldValues>>({
    control,
    name: bankNameSuffixField,
  });

  const selectedBankTypeLabel = useMemo(() => {
    const selected = (bankTypeOptions ?? []).find(option => {
      if (typeof option !== 'object' || option === null) return false;
      return 'value' in option && option.value === bankTypeId;
    });
    const label =
      typeof selected === 'object' && selected !== null && 'label' in selected ? selected.label : '';
    return typeof label === 'string' ? label : '';
  }, [bankTypeId, bankTypeOptions]);

  return `${String(bankName ?? '')}${
    selectedBankTypeLabel && selectedBankTypeLabel !== noneLabel ? selectedBankTypeLabel : ''
  }${String(bankNameSuffix ?? '')}`;
};
